import { Timestamp } from 'firebase/firestore'
import type { Attendee, Course } from 'services/courses'
import type { CourseDashboardEntry, DashboardData } from 'services/dashboard'
import type { Provider } from 'services/providers'
import type { UserProfile } from 'services/profile'

/**
 * Deterministic sample dataset for demo mode.
 *
 * Everything here is generated from a fixed seed so that repeated runs produce
 * identical screens — otherwise visual review compares two different datasets
 * and every diff looks like a change.
 */

/** Mulberry32 — small deterministic PRNG, so the dataset is stable across runs. */
function createRandom(seed: number) {
  let state = seed
  return () => {
    state |= 0
    state = (state + 0x6d2b79f5) | 0
    let t = Math.imul(state ^ (state >>> 15), 1 | state)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const random = createRandom(20240917)

const pick = <T>(items: readonly T[]): T => items[Math.floor(random() * items.length)]

const randomInt = (min: number, max: number) => min + Math.floor(random() * (max - min + 1))

/** The date the dataset is anchored to, so "upcoming" and "past" stay meaningful. */
const TODAY = new Date()

const addDays = (date: Date, days: number) => {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

export const demoProviders: Provider[] = [
  { id: 'prov-safeworks', name: 'SafeWorks Training', color: '#2563EB' },
  { id: 'prov-northline', name: 'Northline Institute', color: '#7C3AED' },
  { id: 'prov-apex', name: 'Apex Skills Group', color: '#0D9488' },
  { id: 'prov-harbourside', name: 'Harbourside Assessors', color: '#C2410C' },
  { id: 'prov-meridian', name: 'Meridian Compliance', color: '#BE185D' },
  { id: 'prov-groundline', name: 'Groundline Consulting', color: '#4D7C0F', inactive: true },
]

const courseTitles = [
  'Work Safely at Heights',
  'Confined Space Entry',
  'Low Voltage Rescue & CPR',
  'Forklift Licence Assessment',
  'Manual Handling Fundamentals',
  'Fire Warden Training',
  'First Aid Refresher',
  'Traffic Management Implementation',
  'Asbestos Awareness',
  'Elevated Work Platform (Yellow Card)',
  'Chemical Handling & Spill Response',
  'Electrical Safety Induction',
]

const consultationTitles = [
  'Site Safety Consultation',
  'Compliance Gap Review',
  'Risk Register Workshop',
  'Incident Investigation Support',
]

const companies = [
  'Ridgeway Construction',
  'Calder Logistics',
  'Brightwater Utilities',
  'Stanmore Manufacturing',
  'Kettle Creek Mining',
  'Aurora Facilities',
  'Pemberton Rail',
  'Halcyon Foods',
]

const firstNames = [
  'Ava','Noah','Mia','Liam','Ruby','Ethan','Chloe','Jack','Isla','Lucas','Grace','Oliver','Zara','Hugo','Nina','Felix',
  'Priya','Marco','Aisha','Declan','Sione','Tayla','Omar','Freya','Rangi','Elena','Kofi','Bridie','Yusuf','Harriet',
]

const lastNames = [
  'Whitfield','Nguyen','Okafor','Petrov','Kaur','Hallberg','Moreau','Tuiloma','Dawson','Bianchi','Osei','Lindqvist',
  'Ferreira','Mackenzie','Rahman','Delgado','Novak','Fitzgerald','Yamada','Abadi','Costa','Bright','Ngata','Salib',
]

/**
 * A short base64 PNG standing in for a captured signature. Real signatures are
 * user data; this is a neutral placeholder so the signature UI has something to
 * render without shipping anyone's actual handwriting.
 */
const PLACEHOLDER_SIGNATURE =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=='

function createAttendee(company: string, options: { noShow?: boolean; signed?: boolean; comment?: string } = {}) {
  const firstName = pick(firstNames)
  const lastName = pick(lastNames)

  const attendee: Attendee = {
    firstName,
    lastName,
    company,
    emailAddress: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${company
      .toLowerCase()
      .replace(/[^a-z]+/g, '')}.example`,
    signature: options.signed === false ? '' : PLACEHOLDER_SIGNATURE,
  }

  if (options.noShow) attendee.noShow = true
  if (options.comment) attendee.comments = options.comment

  return attendee
}

const attendeeComments = [
  'Arrived late, completed all assessment tasks.',
  'Requires interpreter for written assessment.',
  'Transferred from the cancelled 14th session.',
  'Partial completion — practical component outstanding.',
]

function createAttendees(company: string, count: number, noShowCount: number) {
  const attendees: { [key: string]: Attendee } = {}

  for (let index = 0; index < count; index++) {
    const noShow = index < noShowCount
    attendees[`att-${index}-${Math.floor(random() * 1e9).toString(36)}`] = createAttendee(company, {
      noShow,
      // No-shows never signed in, so they must not carry a signature.
      signed: !noShow,
      comment: random() < 0.12 ? pick(attendeeComments) : undefined,
    })
  }

  return attendees
}

function createCourse(index: number, dayOffset: number, overrides: Partial<Course> = {}): Course {
  const provider = pick(demoProviders.filter((candidate) => !candidate.inactive))
  const company = pick(companies)
  const consultation = overrides.consultation ?? false
  const date = addDays(TODAY, dayOffset)

  // Past courses have a full attendance record; upcoming ones are still filling up.
  const isPast = dayOffset < 0
  const capacity = randomInt(6, 18)
  const attendeeCount = isPast ? capacity : randomInt(0, Math.max(1, Math.floor(capacity * 0.7)))
  const noShowCount = isPast && random() < 0.45 ? randomInt(1, 2) : 0

  return {
    id: `course-${index.toString().padStart(3, '0')}`,
    title: consultation ? pick(consultationTitles) : pick(courseTitles),
    company,
    provider: provider.id,
    date: date.toISOString(),
    archive: false,
    consultation,
    attendees: createAttendees(company, attendeeCount, noShowCount),
    ...overrides,
  }
}

function buildCourses(): Course[] {
  const courses: Course[] = []
  let index = 0

  // Upcoming courses — the coordinator's working set.
  for (let day = 1; day <= 45; day += 2) {
    courses.push(createCourse(index++, day))
  }

  // Recent past courses, still active (not yet archived).
  for (let day = -1; day >= -30; day -= 2) {
    courses.push(createCourse(index++, day))
  }

  // Older courses, archived.
  for (let day = -45; day >= -400; day -= 7) {
    courses.push(createCourse(index++, day, { archive: true }))
  }

  // Consultations, which the product tracks alongside courses.
  for (const day of [3, 9, 21, -6, -19, -60]) {
    courses.push(
      createCourse(index++, day, {
        consultation: true,
        consultationEndDate: addDays(TODAY, day + 1).toISOString(),
        archive: day <= -45,
      }),
    )
  }

  // A couple of cancelled courses so that state has real representation.
  courses.push(createCourse(index++, 7, { cancelled: true }))
  courses.push(createCourse(index++, -11, { cancelled: true }))

  // An upcoming course with no attendees yet — the empty state inside a course.
  courses.push(createCourse(index++, 5, { attendees: {} }))

  // A long title and long company name, to stress truncation and wrapping.
  courses.push(
    createCourse(index++, 13, {
      title: 'Work Safely at Heights — Combined Refresher, Rescue & Equipment Inspection (Full Day)',
      company: 'Brightwater Utilities & Infrastructure Services Group',
    }),
  )

  return courses
}

export const demoCourses: Course[] = buildCourses()

export const demoDashboard: DashboardData = {
  updated: Timestamp.fromDate(TODAY),
  courses: demoCourses.reduce<{ [courseID: string]: CourseDashboardEntry }>((entries, course) => {
    const attendees = Object.values(course.attendees ?? {})

    entries[course.id] = {
      date: Timestamp.fromDate(new Date(course.date)),
      title: course.title,
      company: course.company,
      attendees: attendees.length,
      noShows: attendees.filter((attendee) => attendee.noShow).length,
      providerID: course.provider,
      archive: course.archive ?? false,
      cancelled: course.cancelled ?? false,
      consultation: course.consultation ?? false,
      consultationEndDate: course.consultationEndDate
        ? Timestamp.fromDate(new Date(course.consultationEndDate))
        : null,
    }

    return entries
  }, {}),
}

export const demoProfile: UserProfile = {
  patch: 'demo',
  pin: 1234,
  registrationMode: '',
  fcmToken: '',
  fcmTokens: {},
}

export const demoUser = {
  uid: 'demo-user',
  email: 'coordinator@attendix.example',
  displayName: 'Demo Coordinator',
}
